import React, { useState, useEffect } from 'react';
import styles from './BookieDatabase.module.css';
import bookmakersData from '../../data/bookmakers.json';
import api from '../../services/api'; 

const BookieDatabase = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAttribute, setFilterAttribute] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [filteredBookmakers, setFilteredBookmakers] = useState(bookmakersData);
  const [signedUpBookies, setSignedUpBookies] = useState(new Set());
  const [signupFilter, setSignupFilter] = useState('all');

  const PROMO_SPEED_OPTIONS = ['Fast', 'Moderate', 'Slow'];
  const BONUS_CASH_TYPES = ['SNR', 'SR'];

  //function to classify NA values
  const isNA = (value) => {
    return !value || value === 'NA' || value === 'N/A';
  };

  //Find out which bookmakers are signed up to on page load
  useEffect(() => {
    const fetchSignedUpBookies = async () => {
      try {
        const response = await api.get('/api/user/bookies');
        if (Array.isArray(response.data.signedUpBookies)) {
          setSignedUpBookies(new Set(response.data.signedUpBookies));
        } else {
          console.error('Unexpected response format:', response.data);
        }
      } catch (error) {
        console.error('Failed to fetch signed up bookies:', error);
      }
    };
    fetchSignedUpBookies();
  }, []);

  //function to toggle bookie signup status
  const toggleBookieSignup = async (bookieName) => {
    try {
      const newSignedUp = !signedUpBookies.has(bookieName);
      const response = await api.post('/api/user/bookies', {
        bookieName: bookieName,
        signedUp: newSignedUp
      });

      if (response.data.success) {
        setSignedUpBookies(prev => {
          const next = new Set(prev);
          if (newSignedUp) {
            next.add(bookieName);
          } else {
            next.delete(bookieName);
          }
          return next;
        });
      } else {
        console.error('Failed to update bookie status:', response.data.error);
      }
    } catch (error) {
      console.error('Failed to toggle bookie signup:', error);
    }
  };

  //anytime a filter changes, update the results bookmakers
  useEffect(() => {
    let results = bookmakersData;

    //helper function to extract amount value (for dollars)
    const extractAmountValue = (amountStr) => {
        if (isNA(amountStr)) return -1; // NA values go to end
        if (!amountStr.includes('$')) return 0;
        const match = amountStr.match(/\$?(\d+)/);
        return match ? parseInt(match[1]) : -1;
    };

    //helper function to extract amount value (for percentages)
    const extractPercentageValue = (percentStr) => {
        if (isNA(percentStr)) return -1; // Handle "NA" values
        const match = percentStr.match(/(\d+(\.\d+)?)%/); // Extract decimal values
        return match && !isNaN(match[1]) ? parseFloat(match[1]) : 0;
    };
      
      
  
    if (searchTerm) {
      results = results.filter(bookmaker =>
        bookmaker.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
  
    if (signupFilter !== 'all') {
      results = results.filter(bookmaker =>
        signupFilter === 'signed'
          ? signedUpBookies.has(bookmaker.name)
          : !signedUpBookies.has(bookmaker.name)
      );
    }
  
    if (filterAttribute && filterValue) {
      if (filterAttribute === 'gubbingInformation.promoBanSpeed' ||
          filterAttribute === 'depositMatchInfo.typeOfBonusCash') {
        results = results.filter(bookmaker => {
          const path = filterAttribute.split('.');
          const value = path.reduce((obj, key) => obj?.[key], bookmaker);
          return value === filterValue;
        });
      } else if (filterValue === 'on' || filterValue === 'off') {
        results = results.filter(bookmaker => {
          const path = filterAttribute.split('.');
          const value = path.reduce((obj, key) => obj?.[key], bookmaker);
          return filterValue === 'on' ? value : !value;
        });
      } else if (filterValue === 'asc' || filterValue === 'desc') {
        // Filter out "NA" values directly
        let filteredResults = results.filter(bookmaker => {
            const path = filterAttribute.split('.');
            const value = path.reduce((obj, key) => obj?.[key], bookmaker);
            return !isNA(value); // Exclude "NA" values
        });
        
        // Sort the remaining non-NA results
        filteredResults = filteredResults.sort((a, b) => {
            let aValue, bValue;
            if (filterAttribute === 'depositMatchInfo.amount') {
            aValue = extractAmountValue(a.depositMatchInfo.amount);
            bValue = extractAmountValue(b.depositMatchInfo.amount);
            } else if (filterAttribute === 'depositMatchInfo.percentage') {
            aValue = extractPercentageValue(a.depositMatchInfo.percentage);
            bValue = extractPercentageValue(b.depositMatchInfo.percentage);
            console.log(aValue, bValue);
            } else {
            const path = filterAttribute.split('.');
            aValue = path.reduce((obj, key) => obj?.[key], a);
            bValue = path.reduce((obj, key) => obj?.[key], b);
            }
        
            // Ascending or descending sort based on filterValue
            return filterValue === 'asc' ? aValue - bValue : bValue - aValue;
        });
        
        // Set the filtered and sorted results
        results = filteredResults;
      }
    }
  
    setFilteredBookmakers(results);
  }, [searchTerm, filterAttribute, filterValue, signupFilter, signedUpBookies]);

  const booleanAttributes = [
    'websiteSupport',
    'appSupport',
    'depositMatchInfo.offersDepositMatch',
    'promotionOfferings.sportBetReturns',
    'promotionOfferings.racingBetReturns',
    'promotionOfferings.oddboosts',
    'promotionOfferings.depositMatchReloads',
    'mugBetInformation.primingSuggested'
  ];

  //For boolean values convert true/false to Yes/No
  const renderBoolean = (value) => (
    <span className={value ? styles.success : styles.error}>
      {value ? 'Yes' : 'No'}
    </span>
  );

  //For ratings, render stars instead of raw number
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push(<i key={i} className={`fas fa-star ${styles.star}`} />);
      } else if (i - 0.5 === rating) {
        stars.push(<i key={i} className={`fas fa-star-half-alt ${styles.star}`} />);
      } else {
        stars.push(<i key={i} className={`far fa-star ${styles.star}`} />);
      }
    }
    return stars;
  };

  return (
    <div className={styles.bookieDatabaseContainer}>
      <h1>Bookie Database</h1>
      <p className={styles.description}>
        Welcome to the comprehensive Bookie Database. This tool provides detailed information about various bookmakers,
        including their deposit match offers, promotion details, and betting features. Use the search bar to find specific
        bookmakers or filter results using the dropdown menu. Deposit match information is just a guide and you may
        receive a different amount or none at all, deposit amount refers to the deposit maximum and percentage is how much will be matched with a bonus.
        You can tick on/off each bookmaker in the top right of each to keep track of which ones you have signed up to.
      </p>
      <input
        type="text"
        placeholder="Search by Bookmaker name..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        className={styles.searchInput}
      />
        <select
        value={filterAttribute}
        onChange={(e) => {
            setFilterAttribute(e.target.value);
            e.target.classList.toggle(styles.selected, e.target.value !== '');
        }}
        className={`${styles.filterDropdown} ${filterAttribute ? styles.selected : ''}`}
        >
        <option value="" disabled hidden>Select Filter Attribute</option>

        <optgroup label="General Info">
            <option value="overallRating">Overall Rating</option>
            <option value="websiteSupport">Website Support</option>
            <option value="appSupport">App Support</option>
        </optgroup>

        <optgroup label="Deposit Match Info">
        <option value="depositMatchInfo.offersDepositMatch">Offers a Deposit Match</option>
        <option value="depositMatchInfo.amount">Deposit Match Amount</option>
        <option value="depositMatchInfo.percentage">Deposit Match Percentage</option>
        <option value="depositMatchInfo.typeOfBonusCash">Bonus Cash Type</option>
        </optgroup>

        <optgroup label="Promotion Offerings">
        <option value="promotionOfferings.sportBetReturns">Sport Bet Return offered</option>
        <option value="promotionOfferings.racingBetReturns">Racing Bet Returns offered</option>
        <option value="promotionOfferings.oddboosts">Odds Boosts offered</option>
        <option value="promotionOfferings.depositMatchReloads">Deposit Match Reloads offered</option>
        </optgroup>

        <optgroup label="Account Sustainability Information">
        <option value="mugBetInformation.primingSuggested">Priming Suggested?</option>
        <option value="gubbingInformation.promoBanSpeed">Promo Ban Speed</option>
        </optgroup>

        <optgroup label="Other">
        <option value="">No filter</option>
        </optgroup>
      </select>

      {filterAttribute && (
        <select
            value={filterValue}
            onChange={(e) => {
            setFilterValue(e.target.value);
            e.target.classList.toggle(styles.selected, e.target.value !== '');
            }}
            className={`${styles.filterDropdown} ${filterValue ? styles.selected : ''}`}
        >
            <option value="" disabled hidden>Select Filter Value</option>
            {filterAttribute === 'gubbingInformation.promoBanSpeed' ? (
            PROMO_SPEED_OPTIONS.map(speed => (
                <option key={speed} value={speed}>{speed}</option>
            ))
            ) : filterAttribute === 'depositMatchInfo.typeOfBonusCash' ? (
            BONUS_CASH_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
            ))
            ) : booleanAttributes.includes(filterAttribute) ? (
            <>
                <option value="on">Yes</option>
                <option value="off">No</option>
            </>
            ) : (
            <>
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
            </>
            )}
        </select>
        )}

      <select
        value={signupFilter}
        onChange={(e) => {
            setSignupFilter(e.target.value);
            e.target.classList.toggle(styles.selected, e.target.value !== '');
        }}
        className={`${styles.filterDropdown} ${signupFilter ? styles.selected : ''}`}
      >
        <option value="all">Show All Bookies</option>
        <option value="signed">Show Only Signed Up</option>
        <option value="unsigned">Show Only Not Signed Up</option>
      </select>

      {filteredBookmakers.length === 0 ? (
        <div className={styles.noResults}>
            <h2>No results with your current filters or search term</h2>
        </div>
      ) : (
        <div className={styles.bookieList}>
            {filteredBookmakers.map((bookmaker, index) => (
            <div key={index} className={styles.bookieCard}>
                <div
                className={`${styles.signupIndicator} ${signedUpBookies.has(bookmaker.name) ? styles.signed : ''}`}
                onClick={() => toggleBookieSignup(bookmaker.name)}
                >
                <div className={`${styles.circle} ${signedUpBookies.has(bookmaker.name) ? styles.signed : ''}`} />
                </div>
                <h2>{bookmaker.name}</h2>
                <div className={styles.stars}>
                {renderStars(bookmaker.overallRating)}
                </div>
                <p><strong>Parent Company:</strong> {bookmaker.parentCompany}</p>
                <p><strong>Website Support:</strong> {renderBoolean(bookmaker.websiteSupport)}</p>
                <p><strong>Website Link:</strong> <a href={bookmaker.websiteLink} target="_blank" rel="noopener noreferrer">{bookmaker.websiteLink}</a></p>
                <p><strong>App Support:</strong> {renderBoolean(bookmaker.appSupport)}</p>
                <div className={styles.divider}></div>
                <h3>Deposit Match Info</h3>
                <p><strong>Offers a Deposit Match:</strong> {renderBoolean(bookmaker.depositMatchInfo.offersDepositMatch)}</p>
                <p><strong>Amount:</strong> {bookmaker.depositMatchInfo.amount}</p>
                <p><strong>Match Percentage:</strong> {bookmaker.depositMatchInfo.percentage}</p>
                <p><strong>Type of Bonus Cash:</strong> {bookmaker.depositMatchInfo.typeOfBonusCash}</p>
                <p><strong>Turnover Requirement:</strong> {bookmaker.depositMatchInfo.turnoverRequirement}</p>
                <p><strong>Code/Link:</strong> <a href={bookmaker.depositMatchInfo.codeOrLink} target="_blank" rel="noopener noreferrer">{bookmaker.depositMatchInfo.codeOrLink}</a></p>
                <div className={styles.divider}></div>
                <h3>Promotion Offerings</h3>
                <p><strong>Sport Bet Returns:</strong> {renderBoolean(bookmaker.promotionOfferings.sportBetReturns)}</p>
                <p><strong>Racing Bet Returns:</strong> {renderBoolean(bookmaker.promotionOfferings.racingBetReturns)}</p>
                <p><strong>Odds Boosts:</strong> {renderBoolean(bookmaker.promotionOfferings.oddboosts)}</p>
                <p><strong>Other Promotions:</strong> {bookmaker.promotionOfferings.otherPromotions}</p>
                <p><strong>Deposit Match Reloads:</strong> {renderBoolean(bookmaker.promotionOfferings.depositMatchReloads)}</p>
                <div className={styles.divider}></div>
                <h3>Mug-bet Information</h3>
                <p><strong>Suggested Mug Ratio:</strong> {bookmaker.mugBetInformation.suggestedMugRatio}</p>
                <p><strong>Priming Suggested:</strong> {renderBoolean(bookmaker.mugBetInformation.primingSuggested)}</p>
                <p><strong>Priming Length:</strong> {bookmaker.mugBetInformation.primingLength}</p>
                <div className={styles.divider}></div>
                <h3>Gubbing Information</h3>
                <p><strong>Promo Ban Speed:</strong> {bookmaker.gubbingInformation.promoBanSpeed}</p>
                <p><strong>Bonus Cash After Gubbed:</strong> {bookmaker.gubbingInformation.bonusCashAfterGubbed}</p>
                <p><strong>Method of Gubbing:</strong> {bookmaker.gubbingInformation.methodOfGubbing}</p>
                <div className={styles.divider}></div>
                <h3>Other Notes</h3>
                <p>{bookmaker.otherNotes}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookieDatabase;